import { forwardRef, useImperativeHandle, useState } from "react";

import "./stylesAvailablePromotionsModal.css";

import { CloseOutlined } from "@ant-design/icons";

import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatRelativeTime } from "../../../../../utils/dateService";
import DeleteIcon from "../../../../../assets/icons/ic_delete_red.png";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { message } from "antd";

const AvailablePromotionsModal = forwardRef(({ handleCancel, data, disabled }, ref) => {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [availablePromotions, setAvailablePromotions] = useState([]);
  const [deleteId, setDeleteId] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  const getAvailablePromotions = async () => {
    if (!data.user_id) {
      return;
    }

    setIsApiRunning(true);
    const params = {
      user_id: data.user_id,
    };
    try {
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.GET_USER_ACTIVE_BONUSES?.replace(
          "{user_id}",
          params.user_id
        ),
        params
      );
      const list = response.data?.data;

      setAvailablePromotions(list);
    } catch (error) {
      // eslint-disable-next-line no-console -- debug fetch failures
      console.log("error", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  const dateTransform = (timestamp) => {
    if (!timestamp || isNaN(timestamp))
      return AppStrings.NO_EXPIRATION_DATE_AVAILABLE;

    const date = new Date(timestamp * 1000);
    return formatRelativeTime(date, { addSuffix: true });
  };

  const resetDeleteFlow = () => {
    setDeleteId("");
    setDeleteReason("");
  };

  const deletePromotion = async (promotionId) => {
    if (disabled) return;

    const trimmedReason = (deleteReason || "").trim();
    if (!trimmedReason) {
      message.error(
        AppStrings.FIELD_IS_REQUIRED.replace("{title}", AppStrings.REASON),
      );
      return;
    }

    setIsApiRunning(true);

    const payload = {
      bonus_id: promotionId,
      user_id: data.user_id,
      reason: trimmedReason,
    };

    try {
      await PrivateAdminPanelInstance.delete(
        API_ROUTES.EXPIRE_USER_PROMOTION?.replace(
          "{user_id}",
          payload?.user_id
        )?.replace("{promotion_id}", payload?.bonus_id),
        { data: payload }
      );

      await getAvailablePromotions();
      message.success(AppStrings.PROMOTION_SUCCESSFULLY_DELETED);
      resetDeleteFlow();
    } catch (error) {
      message.error(AppStrings.ERROR_DELETING_PROMOTION);
      // eslint-disable-next-line no-console -- debug delete failures
      console.log("error", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  const handleSetDeleteId = (promotionId) => {
    setDeleteReason("");
    if (deleteId === promotionId) {
      setDeleteId("");
      return;
    }
    setDeleteId(promotionId);
  };

  useImperativeHandle(ref, () => ({
    getAvailablePromotions,
    resetDeleteFlow,
  }));

  return (
    <div className="available-promotions-modal-container">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title"> {AppStrings.AVAILABLE_PROMOTIONS}</h2>
        </div>
        <CloseOutlined onClick={handleCancel} className="close-icon" />
      </div>

      <div className="promotion-container">
        {isApiRunning ? (
          <RebetLoader width={100} height={100} />
        ) : (
          <>
            {availablePromotions.map((promotion) => {
              return (
                <div className="promotion" key={promotion.bonus_id}>
                  <div className="delete-container">
                    {!disabled ? (
                      <img
                        className="delete-icon"
                        src={DeleteIcon}
                        alt="delete icon"
                        onClick={() => handleSetDeleteId(promotion.bonus_id)}
                      />
                    ) : (
                      <img
                        className="delete-icon"
                        src={DeleteIcon}
                        alt="delete icon"
                        style={{ opacity: 0.5, cursor: "not-allowed" }}
                      />
                    )}
                  </div>
                  <p className=" promotion-expire-time">
                    {dateTransform(promotion.time_expired_in)}
                  </p>
                  <h3 className="promotion-title">{promotion.banner_title}</h3>
                  <p className=" promotion-description">
                    {promotion.braze_banner_msg}
                  </p>
                  {promotion.bonus_id === deleteId && !disabled && (
                    <div className="confirmation-container">
                      <label
                        className="delete-reason-label"
                        htmlFor={`delete-reason-${promotion.bonus_id}`}
                      >
                        {`${AppStrings.REASON}:`}
                      </label>
                      <textarea
                        id={`delete-reason-${promotion.bonus_id}`}
                        className="delete-reason-input"
                        rows={3}
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder={AppStrings.ENTER_REASON_PLACEHOLDER}
                      />
                      <div className="delete-reason-actions">
                        <button
                          type="button"
                          className="delete-reason-cancel"
                          onClick={resetDeleteFlow}
                        >
                          {AppStrings.CANCEL}
                        </button>
                        <button
                          type="button"
                          className="delete-reason-confirm"
                          onClick={() => deletePromotion(promotion.bonus_id)}
                        >
                          {AppStrings.DELETE}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
});

export default AvailablePromotionsModal;
